import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Sparkles, 
  Brain, 
  MessageSquare, 
  Target, 
  Users, 
  Building, 
  Clock, 
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  Info,
  Minus,
  Plus,
  RotateCcw,
  MousePointer,
  Link,
  Hand,
  ZoomIn,
  ZoomOut,
  Maximize2,
  List,
  Grid3X3
} from 'lucide-react';
import { Workflow, WorkflowStep, WorkflowConnection } from '../types/workflow';
import AIWorkflowService, { AIWorkflowRequest, AIWorkflowResponse } from '../../../services/aiWorkflowService';
import { useToast } from '../../../components/ui/Toast';
import { WorkflowCanvas } from './WorkflowCanvas';

interface AIWorkflowBuilderProps {
  workflow?: Workflow | null;
  onClose: () => void;
  onSave: (workflow: Workflow) => void;
}

interface AIGenerationState {
  isGenerating: boolean;
  progress: number;
  stage: string;
  result?: AIWorkflowResponse;
  error?: string;
}

export const AIWorkflowBuilder: React.FC<AIWorkflowBuilderProps> = ({
  workflow: initialWorkflow,
  onClose,
  onSave
}) => {
  const { addToast } = useToast();
  
  const [workflow, setWorkflow] = useState<Workflow>(
    initialWorkflow || createEmptyWorkflow()
  );
  
  const [aiRequest, setAiRequest] = useState<AIWorkflowRequest>({
    description: '',
    objective: '',
    targetAudience: '',
    industry: '',
    channels: [],
    timeline: '',
    additionalContext: ''
  });
  
  const [aiState, setAiState] = useState<AIGenerationState>({
    isGenerating: false,
    progress: 0,
    stage: 'ready'
  });

  // Canvas state
  const [selectedStep, setSelectedStep] = useState<WorkflowStep | undefined>();
  const [selectedConnection, setSelectedConnection] = useState<WorkflowConnection | undefined>();
  const [canvasMode, setCanvasMode] = useState<'select' | 'connect' | 'pan'>('select');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [viewMode, setViewMode] = useState<'canvas' | 'list'>('canvas');

  const generateWorkflowWithAI = useCallback(async () => {
    if (!aiRequest.description.trim()) {
      addToast({
        type: 'error',
        title: 'Please provide a workflow description'
      });
      return;
    }

    setAiState({
      isGenerating: true,
      progress: 0,
      stage: 'Analyzing your request...'
    });

    try {
      // Simulate progress updates for better UX
      const progressSteps = [
        { progress: 20, stage: 'Understanding requirements...' },
        { progress: 40, stage: 'Designing workflow structure...' },
        { progress: 60, stage: 'Generating steps and connections...' },
        { progress: 80, stage: 'Optimizing workflow logic...' },
        { progress: 100, stage: 'Finalizing workflow...' }
      ];

      for (const step of progressSteps) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setAiState(prev => ({ ...prev, ...step }));
      }

      const response = await AIWorkflowService.generateWorkflow(aiRequest);
      
      setAiState({
        isGenerating: false,
        progress: 100,
        stage: 'completed',
        result: response
      });

      setWorkflow(response.workflow);
      
      if (response.isFallback) {
        addToast({
          type: 'warning',
          title: 'Workflow generation had issues',
          message: `Used fallback workflow. Original error: ${response.originalError}`
        });
      } else {
        addToast({
          type: 'success',
          title: 'Workflow generated successfully!',
          message: response.isSpecific ? 'Specific steps detected' : 'General concept expanded'
        });
      }

    } catch (error) {
      console.error('AI generation failed:', error);
      setAiState({
        isGenerating: false,
        progress: 0,
        stage: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      addToast({
        type: 'error',
        title: 'Failed to generate workflow'
      });
    }
  }, [aiRequest, addToast]);

  const handleSave = useCallback(() => {
    onSave(workflow);
    addToast({
      type: 'success',
      title: 'Workflow saved successfully'
    });
  }, [workflow, onSave, addToast]);

  const resetForm = useCallback(() => {
    setAiRequest({
      description: '',
      objective: '',
      targetAudience: '',
      industry: '',
      channels: [],
      timeline: '',
      additionalContext: ''
    });
    setAiState({
      isGenerating: false,
      progress: 0,
      stage: 'ready'
    });
  }, []);

  // Canvas handlers
  const handleStepAdd = useCallback((step: WorkflowStep) => {
    setWorkflow(prev => ({
      ...prev,
      definition: {
        ...prev.definition,
        steps: [...prev.definition.steps, step]
      }
    }));
  }, []);

  const handleStepUpdate = useCallback((stepId: string, updates: Partial<WorkflowStep>) => {
    setWorkflow(prev => ({
      ...prev,
      definition: {
        ...prev.definition,
        steps: prev.definition.steps.map(step => 
          step.id === stepId ? { ...step, ...updates } : step
        )
      }
    }));
  }, []);

  const handleStepDelete = useCallback((stepId: string) => {
    setWorkflow(prev => ({
      ...prev,
      definition: {
        ...prev.definition,
        steps: prev.definition.steps.filter(step => step.id !== stepId)
      }
    }));
  }, []);

  const handleConnectionAdd = useCallback((connection: WorkflowConnection) => {
    console.log('handleConnectionAdd called with:', connection);
    
    // Add connection to the source step
    setWorkflow(prev => {
      console.log('Previous workflow steps:', prev.definition.steps.map(s => ({ id: s.id, name: s.name, connections: s.connections })));
      
      const updatedWorkflow = {
        ...prev,
        definition: {
          ...prev.definition,
          steps: prev.definition.steps.map(step => 
            step.id === connection.sourceStepId 
              ? { ...step, connections: [...step.connections, connection] }
              : step
          )
        }
      };
      
      console.log('Updated workflow steps:', updatedWorkflow.definition.steps.map(s => ({ id: s.id, name: s.name, connections: s.connections })));
      
      return updatedWorkflow;
    });
  }, []);

  const handleConnectionDelete = useCallback((connectionId: string) => {
    setWorkflow(prev => ({
      ...prev,
      definition: {
        ...prev.definition,
        steps: prev.definition.steps.map(step => ({
          ...step,
          connections: step.connections.filter(conn => conn.id !== connectionId)
        }))
      }
    }));
  }, []);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-neutral-900/50 backdrop-blur-sm border-b border-neutral-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">AI Workflow Builder</h1>
                <p className="text-sm text-neutral-400">
                  Create workflows with AI assistance • {workflow.definition.steps.length} steps generated
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-neutral-800 text-neutral-300 rounded-lg hover:bg-neutral-700 transition-colors"
            >
              Reset
            </button>
            
            {/* View Mode Toggle */}
            <div className="flex bg-neutral-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('canvas')}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${String(viewMode) === 'canvas'
                    ? 'bg-blue-600 text-white'
                    : 'text-neutral-400 hover:text-white'
                  }
                `}
              >
                <Grid3X3 className="w-4 h-4" />
                Canvas
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${String(viewMode) === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-neutral-400 hover:text-white'
                  }
                `}
              >
                <List className="w-4 h-4" />
                List
              </button>
            </div>
            
            {/* Zoom Controls - only show in canvas mode */}
            {viewMode === 'canvas' && (
              <div className="flex items-center bg-neutral-800 rounded-lg p-1">
                <button
                  onClick={() => {
                    const newZoom = Math.max(0.1, zoom - 0.1);
                    setZoom(newZoom);
                    // Center zoom on canvas center
                    const rect = document.querySelector('.workflow-canvas')?.getBoundingClientRect();
                    if (rect) {
                      const centerX = rect.width / 2;
                      const centerY = rect.height / 2;
                      const zoomRatio = newZoom / zoom;
                      const newPanX = centerX - (centerX - pan.x) * zoomRatio;
                      const newPanY = centerY - (centerY - pan.y) * zoomRatio;
                      setPan({ x: newPanX, y: newPanY });
                    }
                  }}
                  className="p-2 text-neutral-400 hover:text-white rounded transition-colors"
                  title="Zoom out"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-2 text-sm text-neutral-300 min-w-[3rem] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={() => {
                    const newZoom = Math.min(3, zoom + 0.1);
                    setZoom(newZoom);
                    // Center zoom on canvas center
                    const rect = document.querySelector('.workflow-canvas')?.getBoundingClientRect();
                    if (rect) {
                      const centerX = rect.width / 2;
                      const centerY = rect.height / 2;
                      const zoomRatio = newZoom / zoom;
                      const newPanX = centerX - (centerX - pan.x) * zoomRatio;
                      const newPanY = centerY - (centerY - pan.y) * zoomRatio;
                      setPan({ x: newPanX, y: newPanY });
                    }
                  }}
                  className="p-2 text-neutral-400 hover:text-white rounded transition-colors"
                  title="Zoom in"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setZoom(1);
                    setPan({ x: 0, y: 0 });
                  }}
                  className="p-2 text-neutral-400 hover:text-white rounded transition-colors"
                  title="Reset view"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            )}
            
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Save Workflow
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden h-full">
        {/* AI Input Panel */}
        <div className="w-96 bg-gradient-to-b from-neutral-900/90 to-neutral-950/90 backdrop-blur-sm border-r border-neutral-800 flex flex-col">
          {/* AI Panel Header */}
          <div className="p-4 border-b border-neutral-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">AI Workflow Assistant</h3>
                <p className="text-sm text-neutral-400">Powered by Gemini LLM</p>
              </div>
            </div>

            {/* AI Status */}
            {aiState.isGenerating && (
              <div className="mb-4 p-3 bg-blue-900/20 border border-blue-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                  <span className="text-sm text-blue-400 font-medium">{aiState.stage}</span>
                </div>
                <div className="w-full bg-neutral-800 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${aiState.progress}%` }}
                  />
                </div>
              </div>
            )}

            {aiState.result && !aiState.result.isFallback && (
              <div className="mb-4 p-3 bg-green-900/20 border border-green-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400 font-medium">Workflow Generated!</span>
                </div>
                <p className="text-xs text-neutral-300">{aiState.result.reasoning}</p>
                <div className="mt-2 text-xs text-neutral-400">
                  Confidence: {Math.round(aiState.result.confidence * 100)}% • 
                  Model: {aiState.result.modelUsed}
                </div>
              </div>
            )}

            {aiState.result && aiState.result.isFallback && (
              <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-yellow-400 font-medium">Fallback Workflow Used</span>
                </div>
                <p className="text-xs text-neutral-300">{aiState.result.reasoning}</p>
                <div className="mt-2 text-xs text-neutral-400">
                  Original error: {aiState.result.originalError}
                </div>
                <div className="mt-1 text-xs text-neutral-400">
                  Confidence: {Math.round(aiState.result.confidence * 100)}% • 
                  Model: {aiState.result.modelUsed}
                </div>
              </div>
            )}

            {aiState.error && (
              <div className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400 font-medium">Generation Failed</span>
                </div>
                <p className="text-xs text-neutral-300">{aiState.error}</p>
              </div>
            )}
          </div>

          {/* AI Input Form */}
          <div className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-4">
              {/* Main Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Workflow Description *
                </label>
                <textarea
                  value={aiRequest.description}
                  onChange={(e) => setAiRequest(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your workflow... Examples:
• 'Send welcome email, wait 2 days, then send follow-up SMS'
• 'Customer onboarding sequence for SaaS platform'
• 'Lead nurturing campaign for real estate'"
                  className="w-full h-32 p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Objective */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  <Target className="w-4 h-4 inline mr-2" />
                  Objective
                </label>
                <input
                  type="text"
                  value={aiRequest.objective}
                  onChange={(e) => setAiRequest(prev => ({ ...prev, objective: e.target.value }))}
                  placeholder="What do you want to achieve?"
                  className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  <Users className="w-4 h-4 inline mr-2" />
                  Target Audience
                </label>
                <input
                  type="text"
                  value={aiRequest.targetAudience}
                  onChange={(e) => setAiRequest(prev => ({ ...prev, targetAudience: e.target.value }))}
                  placeholder="Who is this workflow for?"
                  className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  <Building className="w-4 h-4 inline mr-2" />
                  Industry
                </label>
                <input
                  type="text"
                  value={aiRequest.industry}
                  onChange={(e) => setAiRequest(prev => ({ ...prev, industry: e.target.value }))}
                  placeholder="e.g., SaaS, E-commerce, Healthcare"
                  className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Timeline */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  <Clock className="w-4 h-4 inline mr-2" />
                  Timeline
                </label>
                <input
                  type="text"
                  value={aiRequest.timeline}
                  onChange={(e) => setAiRequest(prev => ({ ...prev, timeline: e.target.value }))}
                  placeholder="e.g., 30 days, 1 week, ongoing"
                  className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Additional Context */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  <Info className="w-4 h-4 inline mr-2" />
                  Additional Context
                </label>
                <textarea
                  value={aiRequest.additionalContext}
                  onChange={(e) => setAiRequest(prev => ({ ...prev, additionalContext: e.target.value }))}
                  placeholder="Any specific requirements, constraints, or preferences..."
                  className="w-full h-20 p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Generate Button */}
              <button
                onClick={generateWorkflowWithAI}
                disabled={!aiRequest.description.trim() || aiState.isGenerating}
                className="w-full p-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
              >
                {aiState.isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Workflow
                  </>
                )}
              </button>

              {/* AI Suggestions */}
              {aiState.result?.suggestions && aiState.result.suggestions.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-yellow-400 font-medium">AI Suggestions</span>
                  </div>
                  <ul className="text-xs text-neutral-300 space-y-1">
                    {aiState.result.suggestions.map((suggestion, index) => (
                      <li key={index}>• {suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Workflow Preview Panel */}
        <div className="flex-1 bg-neutral-950 overflow-hidden">
          {workflow.definition.steps.length > 0 ? (
            <>
              {/* Canvas View */}
              {viewMode === 'canvas' && (
                <div className="flex flex-col h-full">
                  {/* Canvas Controls */}
                  <div className="flex items-center justify-between p-4 bg-neutral-900/50 border-b border-neutral-800">
                    <div className="flex items-center gap-2">
                      {/* Mode Controls */}
                      <div className="flex bg-neutral-800 rounded-lg p-1">
                        <button
                          onClick={() => setCanvasMode('select')}
                          className={`
                            flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                            ${canvasMode === 'select'
                              ? 'bg-blue-600 text-white'
                              : 'text-neutral-400 hover:text-white'
                            }
                          `}
                        >
                          <MousePointer className="w-4 h-4" />
                          Select
                        </button>
                        <button
                          onClick={() => setCanvasMode('connect')}
                          className={`
                            flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                            ${canvasMode === 'connect'
                              ? 'bg-blue-600 text-white'
                              : 'text-neutral-400 hover:text-white'
                            }
                          `}
                        >
                          <Link className="w-4 h-4" />
                          Connect
                        </button>
                        <button
                          onClick={() => setCanvasMode('pan')}
                          className={`
                            flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                            ${canvasMode === 'pan'
                              ? 'bg-blue-600 text-white'
                              : 'text-neutral-400 hover:text-white'
                            }
                          `}
                        >
                          <Hand className="w-4 h-4" />
                          Pan
                        </button>
                      </div>

                      {/* Zoom Controls */}
                      <div className="flex items-center gap-1 ml-4">
                        <button
                          onClick={() => setZoom(Math.max(0.1, zoom - 0.1))}
                          className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
                          title="Zoom Out"
                        >
                          <ZoomOut className="w-4 h-4" />
                        </button>
                        <span className="text-sm text-neutral-400 min-w-[60px] text-center">
                          {Math.round(zoom * 100)}%
                        </span>
                        <button
                          onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                          className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
                          title="Zoom In"
                        >
                          <ZoomIn className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setZoom(1);
                            setPan({ x: 0, y: 0 });
                          }}
                          className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors ml-2"
                          title="Reset View"
                        >
                          <Maximize2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex bg-neutral-800 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('canvas')}
                        className={`
                          flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                          ${String(viewMode) === 'canvas'
                            ? 'bg-blue-600 text-white'
                            : 'text-neutral-400 hover:text-white'
                          }
                        `}
                      >
                        <Grid3X3 className="w-4 h-4" />
                        Canvas
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`
                          flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                          ${String(viewMode) === 'list'
                            ? 'bg-blue-600 text-white'
                            : 'text-neutral-400 hover:text-white'
                          }
                        `}
                      >
                        <List className="w-4 h-4" />
                        List
                      </button>
                    </div>
                  </div>

                  {/* Canvas */}
                  <div className="flex-1 bg-neutral-950 rounded-lg border border-neutral-800">
                    <WorkflowCanvas
                      workflow={workflow}
                      selectedStep={selectedStep}
                      selectedConnection={selectedConnection}
                      mode={canvasMode}
                      zoom={zoom}
                      pan={pan}
                      onStepSelect={setSelectedStep}
                      onStepAdd={handleStepAdd}
                      onStepUpdate={handleStepUpdate}
                      onStepDelete={handleStepDelete}
                      onConnectionSelect={setSelectedConnection}
                      onConnectionAdd={handleConnectionAdd}
                      onConnectionDelete={handleConnectionDelete}
                      onZoomChange={setZoom}
                      onPanChange={setPan}
                    />
                  </div>
                </div>
              )}

              {/* List View */}
              {viewMode === 'list' && (
                <div className="p-6 overflow-y-auto">
                  <div className="max-w-4xl mx-auto">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-white mb-2">Generated Workflow</h2>
                      <p className="text-neutral-400">
                        {workflow.definition.steps.length} steps configured
                      </p>
                    </div>

                    <div className="space-y-4">
                      {/* Workflow Info */}
                      <div className="bg-neutral-900/50 rounded-lg p-4 border border-neutral-800">
                        <h3 className="text-lg font-semibold text-white mb-2">{workflow.name}</h3>
                        <p className="text-neutral-300 mb-3">{workflow.description}</p>
                        <div className="flex items-center gap-4 text-sm text-neutral-400">
                          <span>Status: {workflow.status}</span>
                          <span>Steps: {workflow.definition.steps.length}</span>
                          <span>Triggers: {workflow.triggers.length}</span>
                        </div>
                      </div>

                      {/* Workflow Steps */}
                      <div className="space-y-3">
                        <h4 className="text-lg font-semibold text-white">Workflow Steps</h4>
                        {workflow.definition.steps.map((step: any, index: number) => (
                          <motion.div
                            key={step.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-neutral-900/50 rounded-lg p-4 border border-neutral-800"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h5 className="text-white font-medium">{step.name}</h5>
                                  <span className="px-2 py-1 bg-neutral-800 text-neutral-300 text-xs rounded">
                                    {step.type}
                                  </span>
                                </div>
                                {step.description && (
                                  <p className="text-neutral-400 text-sm mb-2">{step.description}</p>
                                )}
                                {Object.keys(step.config).length > 0 && (
                                  <div className="text-xs text-neutral-500">
                                    <details>
                                      <summary className="cursor-pointer hover:text-neutral-400">
                                        Configuration
                                      </summary>
                                      <pre className="mt-2 p-2 bg-neutral-800 rounded text-xs overflow-x-auto">
                                        {JSON.stringify(step.config, null, 2)}
                                      </pre>
                                    </details>
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Workflow Triggers */}
                      {workflow.triggers.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-lg font-semibold text-white">Triggers</h4>
                          {workflow.triggers.map((trigger: any, index: number) => (
                            <div
                              key={trigger.id}
                              className="bg-neutral-900/50 rounded-lg p-4 border border-neutral-800"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <h5 className="text-white font-medium">{trigger.name}</h5>
                                <span className="px-2 py-1 bg-green-800 text-green-300 text-xs rounded">
                                  {trigger.type}
                                </span>
                              </div>
                              {Object.keys(trigger.config).length > 0 && (
                                <pre className="text-xs text-neutral-400 bg-neutral-800 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(trigger.config, null, 2)}
                                </pre>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full p-6">
              <div className="text-center text-neutral-500">
                <Brain className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
                <h3 className="text-xl font-semibold text-neutral-400 mb-2">
                  Ready to Generate Your Workflow
                </h3>
                <p className="text-neutral-500 mb-6">
                  Describe your workflow in the AI assistant panel to get started
                </p>
                <div className="bg-neutral-900/50 rounded-lg p-4 border border-neutral-800 text-left max-w-md mx-auto">
                  <h4 className="text-white font-medium mb-2">Example Prompts:</h4>
                  <ul className="text-sm text-neutral-400 space-y-1">
                    {[
                      "Send welcome email, wait 2 days, then SMS follow-up",
                      "Customer onboarding for SaaS platform",
                      "Lead nurturing sequence with conditional logic",
                      "Abandoned cart recovery workflow"
                    ].map((prompt, index) => (
                      <li key={`example-prompt-${index}`}>• "{prompt}"</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function createEmptyWorkflow(): Workflow {
  const now = new Date();
  return {
    id: `workflow_${Date.now()}`,
    name: 'New AI Workflow',
    description: 'AI-generated workflow',
    definition: {
      steps: [],
      variables: [],
      settings: {
        maxExecutionTime: 3600000,
        maxRetries: 3,
        errorHandling: 'retry'
      }
    },
    triggers: [],
    isActive: false,
    status: 'draft',
    createdBy: 'ai_assistant',
    createdAt: now,
    updatedAt: now,
    executionCount: 0,
    successRate: 0,
    metadata: {
      generatedBy: 'ai'
    }
  };
} 